import { createUseStyles } from 'react-jss'
import MediaQueries from '../../../styles/MediaQueries'

type GallerySpacing = {
  // Horizontal gutter between slides (applied on both sides); affects peek space.
  slideGutter: number
  // How much of neighboring slides should peek into view on each side.
  imagePeek: number
}

const defaultSpacing: GallerySpacing = {
  imagePeek: 40,
  slideGutter: 14
}

const tabletSpacing: GallerySpacing = {
  imagePeek: 50,
  slideGutter: 25
}

const laptopSmallSpacing: GallerySpacing = {
  imagePeek: 250,
  slideGutter: 25
}

const laptopLargeSpacing: GallerySpacing = {
  imagePeek: 280,
  slideGutter: 25
}

/**
 * Computes a responsive slide width that keeps neighbors peeking in view.
 * @param spacing Layout spacing values: `imagePeek` plus `slideGutter` on each side.
 * @param minPercent Lower bound width as a percentage of the viewport.
 * @param extraPx Extra pixels to give the slide after gutters/peek are subtracted.
 * @param maxPercent Upper bound width as a percentage of the viewport.
 */
const slideWidthForSpacing = (
  spacing: GallerySpacing,
  minPercent: string,
  extraPx: number,
  maxPercent: string
) => {
  const spareSpacePx = spacing.imagePeek * 2 + spacing.slideGutter * 4
  return `clamp(${minPercent}, calc(90% - ${spareSpacePx}px + ${extraPx}px), ${maxPercent})`
}

const useStyles = createUseStyles({
  gallery: {
    width: '100%'
  },
  title: {
    fontFamily: 'GTWalsheim',
    fontWeight: 600,
    fontSize: '24px',
    lineHeight: '100%',
    textAlign: 'center',
    marginBottom: '14px'
  },
  swiper: {
    width: '100%',
    paddingBottom: '20px',
    position: 'relative',
    overflow: 'hidden',
    '& .swiper-wrapper': {
      display: 'flex',
      alignItems: 'stretch'
    },
    '& .swiper-slide': {
      flexShrink: 0,
      height: 'auto',
      display: 'flex',
      justifyContent: 'center'
    }
  },
  slide: {
    width: slideWidthForSpacing(defaultSpacing, '70%', 120, '88%'),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    transition: 'transform 300ms ease, box-shadow 300ms ease'
  },
  activeSlide: {
    transform: 'scale(1.04)',
    zIndex: 2,
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.18)'
  },
  image: {
    width: '100%',
    display: 'block',
    borderRadius: '8px'
  },
  imageDesc: {
    fontSize: '20px',
    fontWeight: '500',
    fontFamily: 'GothamRounded',
    textAlign: 'center',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    padding: '8px'
  },
  [MediaQueries.Tablet]: {
    title: {
      fontSize: '32px'
    },
    slide: {
      width: slideWidthForSpacing(tabletSpacing, '70%', 120, '88%')
    }
  },
  [MediaQueries.LaptopSmall]: {
    slide: {
      width: slideWidthForSpacing(laptopSmallSpacing, '55%', 0, '65%')
    }
  },
  [MediaQueries.LaptopLarge]: {
    slide: {
      width: slideWidthForSpacing(laptopLargeSpacing, '54%', 0, '64%')
    }
  },
  [MediaQueries.Desktop]: {
    slide: {
      width: slideWidthForSpacing(laptopLargeSpacing, '40%', -150, '50%')
    }
  }
})

export default useStyles
